/**
 * The order the writer is about to buy, before any money moves.
 *
 * Renders a `buildOrder` result: an itemized order with a total, or a refusal
 * sentence. The prices are `doc_pricing.json`'s (via `/doc-types`); tax is added
 * by the provider at checkout, which the copy says. The primary button hands the
 * cart to `useCheckout.begin` — the CheckoutDialog then takes over the watching.
 */

import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  makeStyles,
} from "@fluentui/react-components";
import type { FC } from "react";

import { fonts, inkColors } from "../tokens";
import { formatUsd } from "./money";
import type { Order } from "./order";

const useStyles = makeStyles({
  surface: { maxWidth: "420px", width: "calc(100vw - 32px)" },
  body: { display: "flex", flexDirection: "column", gap: "10px" },
  scope: { fontFamily: fonts.body, fontSize: "13px", color: inkColors.ink },
  lines: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    border: "1px solid #ECE7F2",
    borderRadius: "8px",
    padding: "10px 12px",
    maxHeight: "220px",
    overflowY: "auto",
  },
  line: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    fontFamily: fonts.body,
    fontSize: "12.5px",
    color: inkColors.ink,
  },
  lineLabel: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  taxRow: {
    display: "flex",
    justifyContent: "space-between",
    fontFamily: fonts.body,
    fontSize: "11.5px",
    color: inkColors.ink3,
  },
  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    fontFamily: fonts.body,
    fontSize: "14px",
    fontWeight: 600,
    color: inkColors.ink,
    paddingTop: "6px",
    borderTop: "1px solid #ECE7F2",
  },
});

export const OrderDialog: FC<{
  order: Order | null;
  /** True while `begin` is in flight, to disable the button. */
  busy?: boolean;
  onPay: () => void;
  onCancel: () => void;
}> = ({ order, busy = false, onPay, onCancel }) => {
  const styles = useStyles();
  const open = order !== null;

  const scopeLine =
    order === null
      ? ""
      : order.scope === "document"
        ? `This document is ${formatUsd(order.totalMinor)}.`
        : `${order.lines.length === 1 ? "This section is" : `These ${order.lines.length} sections are`} ${formatUsd(order.totalMinor)}.`;

  return (
    <Dialog
      open={open}
      onOpenChange={(_, data) => {
        if (!data.open) onCancel();
      }}
    >
      <DialogSurface className={styles.surface}>
        <DialogBody>
          <DialogTitle>Start drafting</DialogTitle>
          <DialogContent>
            {order !== null && (
              <div className={styles.body}>
                <span className={styles.scope}>{scopeLine}</span>
                <div className={styles.lines}>
                  {order.lines.map((line) => (
                    <div className={styles.line} key={line.section ?? "document"}>
                      <span className={styles.lineLabel}>
                        {line.section === null ? line.label : `${line.section} · ${line.label}`}
                      </span>
                      <span>{formatUsd(line.priceMinor)}</span>
                    </div>
                  ))}
                </div>
                <div className={styles.taxRow}>
                  <span>Tax</span>
                  <span>calculated at payment</span>
                </div>
                <div className={styles.totalRow}>
                  <span>Total</span>
                  <span>{formatUsd(order.totalMinor)}</span>
                </div>
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onCancel} disabled={busy}>
              Not now
            </Button>
            <Button appearance="primary" onClick={onPay} disabled={busy || order === null}>
              {order === null ? "Pay and start drafting" : `Pay ${formatUsd(order.totalMinor)} and start drafting`}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default OrderDialog;
